import { BASE_URL, LOGIN_ENDPOINT, SUBSCRIPTION_STATUS_ENDPOINT } from '@/lib/constants';
import { StateController } from './stateController';

export interface User {
  id: number;
  email: string;
  name?: string;
  is_subscribed: boolean;
  subscription_id?: string | null;
  subscription_expiry_date?: string | null;
  stripe_customer_id?: string | null;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
}

export const authInitialState: AuthState = {
  user: null,
  isLoading: false,
};

class AuthStateController extends StateController<AuthState> {
  constructor(initialState: AuthState) {
    super(initialState, AuthStateController.name);
    this.autoBind(this);
  }

  initialize(stateToUpdate: Partial<AuthState> = {}): void {
    this.updateState({
      ...authInitialState,
      ...stateToUpdate,
    });
  }

  // User management methods
  async login(
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: User }> {
    try {
      this.updateState({ isLoading: true });

      const response = await fetch(`${BASE_URL}${LOGIN_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();

        const userData: User = {
          id: data.user_id,
          email: email,
          is_subscribed: data.is_subscribed || false,
          subscription_id: data.subscription_id,
          subscription_expiry_date: data.subscription_expiry_date
            ? new Date(data.subscription_expiry_date).toISOString()
            : null,
        };

        this.updateState({ user: userData, isLoading: false });
        localStorage.setItem('user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        this.updateState({ isLoading: false });
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error: any) {
      this.updateState({ isLoading: false });
      return { success: false, error: error.message || 'Network error occurred' };
    }
  }

  async signup(
    name: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: User }> {
    try {
      this.updateState({ isLoading: true });

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/user/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, password }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        const userData: User = {
          id: data.user_id,
          email: email,
          name: name,
          is_subscribed: data.is_subscribed || false,
        };

        this.updateState({ user: userData, isLoading: false });
        localStorage.setItem('user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        this.updateState({ isLoading: false });
        return { success: false, error: errorData.detail || 'Signup failed' };
      }
    } catch (error: any) {
      this.updateState({ isLoading: false });
      return { success: false, error: error.message || 'Network error occurred' };
    }
  }

  logout(): void {
    this.updateState({ user: null, isLoading: false });
    localStorage.removeItem('user');
  }

  updateSubscriptionStatus(subscriptionData: {
    is_subscribed: boolean;
    subscription_id?: string | null;
    subscription_expiry_date?: string | null;
  }): void {
    const currentUser = this.getValue('user') as User | null;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...subscriptionData };
      this.updateState({ user: updatedUser });
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }

  // Initialize user from localStorage
  initializeFromStorage(): void {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        this.fetchSubscriptionStatus(userData);
        this.updateState({ user: userData, isLoading: false });
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
        this.updateState({ isLoading: false });
      }
    } else {
      this.updateState({ isLoading: false });
    }
  }

  async fetchSubscriptionStatus(user: any): Promise<void> {
    if (!user?.id) return Promise.resolve();

    try {
      const response = await fetch(
        `${BASE_URL}${SUBSCRIPTION_STATUS_ENDPOINT}?user_id=${user.id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Update auth controller
        this.updateSubscriptionStatus({
          is_subscribed: data.is_subscribed,
          subscription_id: data.subscription_id,
          subscription_expiry_date: data.subscription_expiry_date
            ? new Date(data.subscription_expiry_date).toISOString()
            : null,
        });
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  }
}

export const authController = new AuthStateController(authInitialState);
